import type { HorarioSemanal } from '@laser/core';
import { instanteDesdeZona, partesEnZona } from './fechas';

export type Intervalo = [number, number]; // ms UTC [inicio, fin)

export const DIAS_SEMANA = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'] as const;
export const DIAS_CORTOS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'] as const;

export const horarioVacio = (): HorarioSemanal => ({ 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] });

export function aMinutos(hhmm: string): number {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return NaN;
  return +m[1] * 60 + +m[2];
}

/** Rango válido: HH:MM bien formados y fin > inicio (24:00 permitido como fin). */
export function rangoValido([a, b]: [string, string]): boolean {
  const x = aMinutos(a), y = aMinutos(b);
  return Number.isFinite(x) && Number.isFinite(y) && x >= 0 && y <= 24 * 60 && y > x;
}

/** Intervalos de trabajo de la máquina que tocan [desde, hasta), recortados. */
export function intervalosLaborales(horario: HorarioSemanal, desde: number, hasta: number): Intervalo[] {
  const out: Intervalo[] = [];
  if (hasta <= desde) return out;
  const p = partesEnZona(desde);
  // Empieza un día antes por rangos que cruzan medianoche en la zona (defensivo).
  let y = p.anio, mo = p.mes, d = p.dia - 1;
  for (let i = 0; i < 400; i++) {
    const base = new Date(Date.UTC(y, mo - 1, d + i));
    const anio = base.getUTCFullYear(), mes = base.getUTCMonth() + 1, dia = base.getUTCDate();
    const inicioDia = instanteDesdeZona(anio, mes, dia);
    if (inicioDia >= hasta) break;
    const ds = base.getUTCDay() as keyof HorarioSemanal;
    for (const rango of horario[ds] ?? []) {
      if (!rangoValido(rango)) continue;
      const ma = aMinutos(rango[0]), mb = aMinutos(rango[1]);
      const ini = instanteDesdeZona(anio, mes, dia, Math.floor(ma / 60), ma % 60);
      const fin = mb === 24 * 60
        ? instanteDesdeZona(anio, mes, dia + 1)
        : instanteDesdeZona(anio, mes, dia, Math.floor(mb / 60), mb % 60);
      const a = Math.max(ini, desde), b = Math.min(fin, hasta);
      if (b > a) out.push([a, b]);
    }
  }
  return out.sort((u, v) => u[0] - v[0]);
}

export function minutosLaborales(horario: HorarioSemanal, desde: number, hasta: number): number {
  return intervalosLaborales(horario, desde, hasta).reduce((s, [a, b]) => s + (b - a), 0) / 60_000;
}

/** Horas por semana del horario (para mostrar en el catálogo). */
export function horasPorSemana(horario: HorarioSemanal): number {
  let min = 0;
  for (let d = 0 as keyof HorarioSemanal; d <= 6; d = (d + 1) as keyof HorarioSemanal) {
    for (const r of horario[d] ?? []) if (rangoValido(r)) min += aMinutos(r[1]) - aMinutos(r[0]);
  }
  return min / 60;
}

/** Resumen corto: "lun–vie 09:00–18:00 · sáb 10:00–14:00". */
export function resumenHorario(horario: HorarioSemanal): string {
  const clave = (d: keyof HorarioSemanal) => (horario[d] ?? []).map((r) => r.join('–')).join(', ');
  const grupos: { desde: number; hasta: number; texto: string }[] = [];
  for (const d of [1, 2, 3, 4, 5, 6, 0] as (keyof HorarioSemanal)[]) {
    const t = clave(d);
    const ult = grupos[grupos.length - 1];
    if (ult && ult.texto === t && t) ult.hasta = d;
    else grupos.push({ desde: d, hasta: d, texto: t });
  }
  const partes = grupos.filter((g) => g.texto).map((g) =>
    (g.desde === g.hasta ? DIAS_CORTOS[g.desde] : `${DIAS_CORTOS[g.desde]}–${DIAS_CORTOS[g.hasta]}`) + ' ' + g.texto);
  return partes.length ? partes.join(' · ') : 'sin horario';
}

/** Suma minutos de trabajo a partir de `desde` respetando el horario. Devuelve el instante final. */
export function avanzarLaboral(horario: HorarioSemanal, desde: number, minutos: number, limiteDias = 120): number | null {
  let restante = minutos * 60_000;
  const hasta = desde + limiteDias * 86_400_000;
  for (const [a, b] of intervalosLaborales(horario, desde, hasta)) {
    const dur = b - a;
    if (restante <= dur) return a + restante;
    restante -= dur;
  }
  return null;
}

/** Día de la semana (CDMX) del instante. */
export const diaSemana = (t: number) => partesEnZona(t).diaSemana;
